import React from 'react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { ExternalLink, Github, Calendar, Award } from 'lucide-react';

const ProjectsSection = () => {
  const projects = [
    {
      title: 'CT-Eye Analytics Platform',
      description: 'Healthcare analytics platform for medical imaging analysis using AI/ML algorithms and real-time data processing.',
      company: 'UST (Philips)',
      period: '2024',
      category: 'Healthcare Technology',
      achievements: [
        '40% improvement in data ingestion throughput',
        '30% reduction in release cycle time',
        'Real-time medical data processing',
        'AI-powered diagnostic insights'
      ],
      technologies: ['Spring Boot', 'ReactJS', 'AWS Lambda', 'S3', 'TensorFlow', 'Healthcare APIs'],
      color: 'from-blue-500 to-indigo-600',
      icon: '🏥'
    },
    {
      title: 'Material Shortage Planning (MSP)',
      description: 'Predictive analytics application for supply chain optimization and material shortage prevention in enterprise environments.',
      company: 'Quest Global (HPE)',
      period: '2022-2023',
      category: 'Enterprise Software',
      achievements: [
        'Predictive shortage analytics integration',
        '85% code coverage with comprehensive testing',
        'Modular microservices architecture',
        'AWS CloudFormation for scalability'
      ],
      technologies: ['Java Spring Boot', 'ReactJS', 'AWS CloudFormation', 'Elasticsearch', 'Predictive Analytics'],
      color: 'from-green-500 to-emerald-600',
      icon: '📊'
    },
    {
      title: 'AutoAudit Quality Management',
      description: 'AI-powered quality management software for automated auditing and compliance monitoring with real-time reporting.',
      company: 'Forward AI',
      period: '2021-2022',
      category: 'AI & Automation',
      achievements: [
        '25% decrease in error rates',
        'Circuit-breaker patterns for reliability',
        'Automated compliance monitoring',
        'Real-time audit reporting'
      ],
      technologies: ['Node.js', 'Express.js', 'ReactJS', 'AWS EC2', 'S3', 'AI/ML Models'],
      color: 'from-purple-500 to-violet-600',
      icon: '🤖'
    },
    {
      title: 'CustodyRx Hospital Asset Management',
      description: 'Comprehensive hospital asset tracking and management system for healthcare institutions with inventory optimization.',
      company: 'Bacancy Technologies',
      period: '2021',
      category: 'Healthcare Systems',
      achievements: [
        '20% reduction in resource misplacement',
        'Improved inventory accountability',
        'Comprehensive reporting features',
        'Real-time asset tracking'
      ],
      technologies: ['.NET Core', 'ReactJS', 'MySQL', 'Asset Tracking', 'Healthcare Systems'],
      color: 'from-orange-500 to-red-600',
      icon: '🏥'
    },
    {
      title: 'Kore Wireless Network Management',
      description: 'Network management platform for wireless infrastructure monitoring and optimization with real-time analytics.',
      company: 'Clicklabs',
      period: '2020-2021',
      category: 'Network Infrastructure',
      achievements: [
        'Real-time network monitoring',
        'Performance optimization algorithms',
        'Scalable wireless management',
        'Analytics dashboard'
      ],
      technologies: ['ReactJS', 'Node.js', 'Network APIs', 'Real-time Analytics', 'IoT'],
      color: 'from-cyan-500 to-blue-600',
      icon: '📡'
    },
    {
      title: 'English Learning Application',
      description: 'Interactive language learning platform with role-based access, examination modules, and progress tracking.',
      company: 'Travancore Analytics',
      period: '2018-2020',
      category: 'Education Technology',
      achievements: [
        'User role management system',
        'Interactive exam modules',
        'Progress tracking dashboard',
        'Scalable learning architecture'
      ],
      technologies: ['ReactJS', 'Node.js', 'Educational APIs', 'User Management', 'Analytics'],
      color: 'from-pink-500 to-rose-600',
      icon: '📚'
    }
  ];

  return (
    <section id="projects" className="py-20 relative">
      <div className="container mx-auto px-6 z-10 relative">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            Featured <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">Projects</span>
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Showcasing impactful solutions across healthcare, enterprise software, AI/ML, and network infrastructure domains
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 max-w-6xl mx-auto">
          {projects.map((project, index) => (
            <Card key={index} className="bg-gray-800/40 border-gray-700/50 backdrop-blur-sm hover:bg-gray-800/60 transition-all duration-300 hover:scale-105 overflow-hidden">
              <div className="p-8">
                {/* Header */}
                <div className="flex items-start justify-between mb-6">
                  <div className="flex items-center">
                    <span className="text-2xl mr-3">{project.icon}</span>
                    <div>
                      <h3 className="text-xl font-bold text-white mb-1">{project.title}</h3>
                      <div className="flex items-center space-x-4 text-sm text-gray-400">
                        <span className="font-medium text-blue-400">{project.company}</span>
                        <div className="flex items-center">
                          <Calendar className="w-3 h-3 mr-1" />
                          {project.period}
                        </div>
                      </div>
                    </div>
                  </div>
                  <Badge className={`bg-gradient-to-r ${project.color} text-white px-3 py-1 text-xs`}>
                    {project.category}
                  </Badge>
                </div>

                {/* Description */}
                <p className="text-gray-300 mb-6 leading-relaxed">
                  {project.description}
                </p>

                {/* Key Achievements */}
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-white mb-3 flex items-center">
                    <Award className="w-4 h-4 mr-2 text-green-400" />
                    Key Impact
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                    {project.achievements.slice(0, 4).map((achievement, achIndex) => (
                      <div key={achIndex} className="flex items-start text-gray-300 text-sm">
                        <span className="text-green-400 mr-2 mt-1">•</span>
                        <span>{achievement}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Technologies */}
                <div className="pt-4 border-t border-gray-700/50">
                  <div className="flex flex-wrap gap-2">
                    {project.technologies.map((tech, techIndex) => (
                      <Badge 
                        key={techIndex} 
                        variant="outline" 
                        className="border-gray-600 text-gray-300 hover:border-blue-400 hover:text-blue-400 transition-colors duration-200 text-xs"
                      >
                        {tech}
                      </Badge>
                    ))}
                  </div>
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Project Stats */}
        <div className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-8">
          <Card className="bg-gray-800/40 border-gray-700/50 backdrop-blur-sm text-center p-6">
            <div className="text-3xl font-bold bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent mb-2">20+</div>
            <div className="text-gray-400 text-sm">Projects Delivered</div>
          </Card>
          
          <Card className="bg-gray-800/40 border-gray-700/50 backdrop-blur-sm text-center p-6">
            <div className="text-3xl font-bold bg-gradient-to-r from-green-400 to-emerald-400 bg-clip-text text-transparent mb-2">99.9%</div>
            <div className="text-gray-400 text-sm">Success Rate</div>
          </Card>
          
          <Card className="bg-gray-800/40 border-gray-700/50 backdrop-blur-sm text-center p-6">
            <div className="text-3xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent mb-2">6+</div>
            <div className="text-gray-400 text-sm">Industries</div>
          </Card>
          
          <Card className="bg-gray-800/40 border-gray-700/50 backdrop-blur-sm text-center p-6">
            <div className="text-3xl font-bold bg-gradient-to-r from-orange-400 to-red-400 bg-clip-text text-transparent mb-2">40%</div>
            <div className="text-gray-400 text-sm">Avg Improvement</div>
          </Card>
        </div>
      </div>
    </section>
  );
};

export default ProjectsSection;